import { reportDiag } from '@/api';

/**
 * Browser errors during a call, for Admin → What happened? (docs/SPEC_V14_DIAG.md).
 * Only ids, the component and the error message (≤ 120 characters) — never
 * what anyone typed or said. The same error is sent once per page, and at
 * most MAX_REPORTS in all, so a render loop can't flood the trail.
 */
export type ClientErrorKind = 'game_error' | 'screen_error' | 'script_error';

const MAX_REPORTS = 10;
const sent = new Set<string>();
let currentCall: string | null = null;

/** The call on screen (CallRoom sets it), so any error can be tied to it. */
export function setCurrentCall(sessionId: string | null): void {
  currentCall = sessionId;
}

export function currentCallId(): string | null {
  return currentCall;
}

/** "at DoodleCanvas (…)" / "in DoodleCanvas" → "DoodleCanvas" (the innermost component). */
export function componentOf(stack: string | null | undefined): string | null {
  const m = /(?:at|in) ([A-Z][A-Za-z0-9_$]*)/.exec(stack ?? '');
  return m?.[1] ?? null;
}

export function reportClientError(kind: ClientErrorKind, where: string | null, err: unknown): void {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : 'unknown error';
  const detail = `${where ?? 'unknown'}: ${msg}`.replace(/\s+/g, ' ').slice(0, 120);
  const key = `${kind}|${detail}`;
  if (sent.has(key) || sent.size >= MAX_REPORTS) return;
  sent.add(key);
  reportDiag({ kind, session_id: currentCall ?? undefined, detail });
}

/** Tests only. */
export function resetClientErrors(): void {
  sent.clear();
  currentCall = null;
}

let installed = false;

/**
 * Errors outside React's render (a pointer handler, a timer, a rejected
 * promise) don't reach an error boundary: the screen keeps going but the
 * thing quietly stops working. Report them while a call is on screen.
 */
export function installCallErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    if (!currentCall) return;
    const file = e.filename ? e.filename.split('/').pop()?.split('?')[0] : null;
    reportClientError('script_error', file ? `${file}:${e.lineno}` : null, e.error ?? e.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (!currentCall) return;
    reportClientError('script_error', 'promise', e.reason);
  });
}
